/**
 * THE RENDER POLICY — one rule for every surface that draws a world: the web TV receivers (Android TV, LG,
 * Samsung), the phone surface (the iOS / Android feed's WebView), the web + desktop Visualizer, and the
 * Apple TV host (apps/tvos/glspike bundles this file).
 *
 * Owner, 2026-10-27: "resolution should never drop below the top resolution a TV can show … figure out the
 * setting that lets our visuals run at it, still smooth, ONLY lowering detail. We cannot compromise on
 * resolution or responsivity." So:
 *  - RESOLUTION IS NOT A LEVER. Every surface draws at the full device-pixel size its platform gives it (window
 *    × devicePixelRatio, capped only by the GPU's largest drawable) — nothing here can lower it.
 *  - DETAIL IS THE ONLY LEVER (the SDK's live control, engineApi 1.20: fewer objects resampled across the whole
 *    shape, fewer shader steps — never the picture's extent). Per device and per world it is CALIBRATED once:
 *    the highest detail that meets the surface's smoothness bar at native resolution — "the setting" — and
 *    remembered (worldCost.ts), re-calibrated when the engine or this policy changes.
 *  - SMOOTHNESS is a bar: a p95 frame interval, measured on the WALL CLOCK (a 2 fps world must be judged as
 *    quickly as a 60 fps one). A world that misses it even at its minimum detail is HEAVY on that device: it
 *    leaves the lists and the Director's pool, and a TV tells the phone casting to it ("Not available on <TV>").
 *  - RESPONSIVITY is untouched: no buffering, render-ahead or frame pacing — the picture answers the audio on
 *    the next frame drawn, whatever the detail.
 *
 * Dependency-free on purpose: the Apple TV's JavaScriptCore host bundles it too.
 */

/**
 * A smoothness bar: the p95 frame interval must be at or under maxP95Ms. A 60 Hz panel shows a frame for 1, 2,
 * 3… vsyncs (16.7 / 33.3 / 50 ms), plus timer jitter; each surface's bar is set from what reads as smooth there.
 */
export interface SmoothBar { maxP95Ms: number }

export interface SurfacePolicy {
  bar: SmoothBar
  /** settled seconds before a world is measured: its first frames carry its build stalls */
  warmupSec: number
  /** THE VOICE ORBS ("Add orbs that follow the voices" — VisualHost.setOrb, engineApi 1.23) on this surface, or
   *  null where they never draw. Owner decision (2026-10-30): orbs are for the screen in the user's hand — the
   *  phone, the web, the desktop — and NEVER reach a TV, whatever a sender, a query or a stray field says. */
  orb: { size: number } | null
}

/** What the orbs follow on a device. The full orb rides the voice's PITCH (height) and stereo PAN — the SDK's
 *  per-block analysis in pushOrbPcm, measured 2026-10-30 at ~0.2 ms a 1,024-sample block on an M1 Max and
 *  ~2.8 ms (≈ 133 ms/s at ~47 blocks/s) on a main thread like a 4×-throttled M1: a weak phone's. There the orb
 *  still follows the voice (presence, energy, phrasing — frame.drivers) without the per-block analysis. */
export interface OrbDevice {
  /** the phone's shell declares a weak phone (?weakphone=1 — receiver/weakTier declaredWeakPhone) */
  weakPhone: boolean
}
export const CAPABLE_DEVICE: OrbDevice = { weakPhone: false }

export interface OrbOptions {
  enabled: boolean
  size?: number
  count?: 1
  /** fed the audio's stereo blocks (VisualHost.pushOrbPcm) — pitch height + pan; off, the orb follows the drivers */
  followsPitch?: boolean
}

/**
 * The surfaces. Thresholds are measured, not guessed (the EcoTV sweeps — 1 GB Android TV, 60 Hz): the light 3D
 * worlds there run 30–41 fps with p95 34–50 ms; what reads as choppy sits at ≤ 22 fps / p95 ≥ 57 ms.
 *  - tv: frames of 2–3 vsyncs read as smooth across a room → p95 ≤ 55 ms (no frame past 3 vsyncs).
 *  - phone: a hand-held screen shows judder sooner → p95 ≤ 42 ms (the 95th frame is never a 3-vsync stall).
 *  - desktop: a computer has headroom → p95 ≤ 34 ms (frames of 1–2 vsyncs).
 *  - tvos: an Apple TV across the room: the TV's bar, on the REAL frame interval.
 */
export type Surface = 'tv' | 'phone' | 'desktop' | 'tvos'
export const SURFACES: Record<Surface, SurfacePolicy> = {
  tv: { bar: { maxP95Ms: 55 }, warmupSec: 4, orb: null },
  phone: { bar: { maxP95Ms: 42 }, warmupSec: 3, orb: { size: 0.18 } },
  desktop: { bar: { maxP95Ms: 34 }, warmupSec: 2.5, orb: { size: 0.18 } },
  tvos: { bar: { maxP95Ms: 55 }, warmupSec: 2, orb: null }
}

/** The orb options a surface draws with when its user `wants` them, on this `device` — the ONE place a host's
 *  setOrb, and whether the orbs are fed the audio's blocks, come from. One orb (2–3 is the SDK's experimental
 *  identity tracking), the owner's size (≈3.5× the SDK default), the SDK's tail/light defaults; the voice's pitch
 *  and pan except on a weak phone (OrbDevice). A TV surface resolves to disabled, always. */
export function orbFor(surface: Surface, wants: boolean, device: OrbDevice = CAPABLE_DEVICE): OrbOptions {
  const p = SURFACES[surface].orb
  if (!wants || !p) return { enabled: false }
  return { enabled: true, size: p.size, count: 1, followsPitch: !(surface === 'phone' && device.weakPhone) }
}

/** Bump when a bar or the calibration changes: every device's remembered settings were found under the old rule
 *  and are calibrated again (worldCost.ts keys its store by it). 4: native resolution, detail the only lever. */
export const POLICY_VERSION = 4

/** The native drawable for a surface of `cssW × cssH` CSS px at `dpr`, capped only by the GPU's largest
 *  drawable (`maxDim`, e.g. MAX_VIEWPORT_DIMS): the long edge is scaled down to fit, aspect kept. */
export function nativeDrawable(cssW: number, cssH: number, dpr: number, maxDim = Infinity): { w: number; h: number } {
  const w = Math.max(1, Math.round(cssW * (dpr || 1)))
  const h = Math.max(1, Math.round(cssH * (dpr || 1)))
  const k = Math.min(1, maxDim / Math.max(w, h))
  return { w: Math.max(1, Math.floor(w * k)), h: Math.max(1, Math.floor(h * k)) }
}

/** Frame intervals over a stretch of WALL-CLOCK time: their p95, rate and span. */
export class FrameWindow {
  private ms: number[] = []
  private spanSec = 0
  push(realDtSec: number): void {
    this.ms.push(realDtSec * 1000)
    this.spanSec += realDtSec
  }
  clear(): void { this.ms = []; this.spanSec = 0 }
  get frames(): number { return this.ms.length }
  get span(): number { return this.spanSec }
  get fps(): number { return this.spanSec > 0 ? this.ms.length / this.spanSec : 0 }
  /** the 95th-percentile frame interval in ms (nearest rank), 0 for no frames */
  p95(): number {
    if (!this.ms.length) return 0
    const sorted = [...this.ms].sort((a, b) => a - b)
    return sorted[Math.min(sorted.length - 1, Math.ceil(0.95 * sorted.length) - 1)]
  }
  /** frames over `barMs` */
  over(barMs: number): number { let n = 0; for (const m of this.ms) if (m > barMs) n++; return n }
}

/**
 * THE DETAIL LEVER — exactly the SDK's live control on the world on stage (engineApi 1.20, syqel-sdk
 * docs/DETAIL_POLICY.md: VisualHost.activeDetail / activeDetailRange / setActiveDetail). Lower detail = fewer
 * instances resampled across the whole shape, fewer shader iterations (uLod / syq_Lod) — never the picture's
 * extent, never resolution (resolution is native — not a lever). A range with min === max is a world with no lever.
 * Every web surface passes its VisualHost, which IS a DetailControl; NO_DETAIL is a host without one.
 */
export interface DetailControl {
  activeDetail: () => number
  activeDetailRange: () => { min: number; max: number }
  setActiveDetail: (d: number) => void
  /** what is on stage ([on-stage, incoming?]) — the calibrator aborts on a cut or a cross-fade (VisualHost has it) */
  activeModes?: () => readonly string[]
  /** changes when the on-stage renderer swaps its program (an fx visual's specialised program landing) — the
   *  calibrator re-settles instead of scoring the swap (VisualHost has it) */
  activeProgramEpoch?: () => number
}

/** No live detail control (every world, until the engine pin carries one): range {1,1} — no lever. */
export const NO_DETAIL: DetailControl = { activeDetail: () => 1, activeDetailRange: () => ({ min: 1, max: 1 }), setActiveDetail: () => {} }

/**
 * Whether a window of frames meets a bar — decided on the wall clock, conclusively on BOTH sides. `null` = not yet.
 *  - FAIL fast: even if the frames go on arriving at their observed rate, more than 5% of an EXTENDED window's
 *    frames are already over the line — its p95 can only end over it (a 2 fps world fails in about a second).
 *  - PASS only with margin: at SPAN_SEC, p95 ≤ (1 − MARGIN) × bar. Clearly over (p95 > (1 + MARGIN) × bar) fails.
 *  - NEAR THE BAR (between the two): the window extends — bounded by EXTEND_SEC — and the whole extended window
 *    decides. One 3 s window of a world sitting at the bar is a coin toss: judged on it, the same world reads smooth
 *    now and heavy ten seconds later and the TV's offer flips (the Apple TV's coral-reef: 54 ms against a 55 ms
 *    bar, then 56–58).
 * `standing` — judging a setting already found (the governor's watch): it is LOST only past (1 + MARGIN) × bar, so a
 * world found at the bar keeps it while it stays within the band — hysteresis, the verdict doesn't flicker.
 */
export function judgeWindow(w: FrameWindow, barMs: number, standing = false): boolean | null {
  if (w.span < WINDOW.MIN_SPAN_SEC || !w.frames) return null
  const line = standing ? (1 + WINDOW.MARGIN) * barMs : barMs // the extended window's decision line
  const expected = w.frames + w.fps * Math.max(0, WINDOW.EXTEND_SEC - w.span)
  if (w.over(line) > 0.05 * expected) return false
  if (w.span >= WINDOW.EXTEND_SEC) return w.p95() <= line // the whole bounded window (and a world giving almost no frames)
  if (w.span < WINDOW.SPAN_SEC || w.frames < WINDOW.MIN_FRAMES) return null
  const p95 = w.p95()
  if (p95 <= (1 - WINDOW.MARGIN) * barMs) return true
  if (p95 > (1 + WINDOW.MARGIN) * barMs) return false
  return null // near the bar: keep measuring
}
/** The measurement window: never decided before MIN_SPAN_SEC; a clear verdict at SPAN_SEC; a world near the bar
 *  (within ±MARGIN of it) measured on until EXTEND_SEC; a lever's move settles for SETTLE_SEC before its frames
 *  count. A standing setting is re-calibrated
 *  only after WATCH_FAILS conclusive misses IN A ROW (sustained evidence — one busy passage is not a verdict). */
export const WINDOW = { MIN_SPAN_SEC: 1, SPAN_SEC: 3, EXTEND_SEC: 9, MARGIN: 0.1, MIN_FRAMES: 3, SETTLE_SEC: 0.5, WATCH_FAILS: 2 }

/** A CONCLUSIVE FAR MISS: a heavy verdict whose p95 is at least this many times the bar (the SDK's farMissFactor,
 *  DetailCalibrator) — not a world near the bar but one that plays as a slideshow. Such a world leaves the screen
 *  at once (worldCost.farHeavyHere → catalogue.tvStillPlayable), where one near the bar keeps it until the next pick. */
export const FAR_MISS = 1.5

/** Is this verdict a CONCLUSIVE FAR MISS on `policy`'s surface — heavy (no detail meets the bar) with its p95 at least
 *  FAR_MISS × the bar? The one rule every host keeps or leaves a pinned world by (worldCost.farHeavyHere on the web
 *  hosts; glspike's verdicts on the Apple TV). */
export function farMiss(c: Pick<Calibration, 'detail' | 'p95Ms'>, policy: SurfacePolicy): boolean {
  return c.detail === null && c.p95Ms >= FAR_MISS * policy.bar.maxP95Ms
}

/** A calibration's outcome: the highest detail meeting the bar at native resolution, or null — the world misses
 *  it even at its minimum detail (HEAVY on this device). */
export interface Calibration { detail: number | null; p95Ms: number; fps: number }

/**
 * THE CALIBRATION SEAM — what finds "the setting" for the world on stage. Fed each settled frame's real interval
 * with whether it was CLEAN (no world built, compiled or torn down across it — LifecycleLedger; an unclean
 * interval is discarded, never scored); returns the outcome once — or `null` when the run was abandoned (the
 * stage changed under it) — and undefined until then.
 */
export interface Calibrator {
  tick: (realDtSec: number, clean: boolean) => Calibration | null | undefined
  cancel: () => void
}
/** Starts a calibration of the world on stage against a p95 bar (ms), from the detail it is at now. */
export type CalibratorFactory = (target: DetailControl, barMs: number) => Calibrator

/** The SDK's calibrator class (engineApi 1.21, @syqel/engine DetailCalibrator) — the shape this module needs of it,
 *  so it stays dependency-free (the Apple TV bundles it); every host passes its engine's class to sdkCalibrator. */
export interface DetailCalibratorClass {
  new (target: DetailControl, opts: {
    bar: { p95Ms: number }
    startDetail?: number
    settleSec?: number
    sampleSec?: number
    maxSampleSec?: number
    passMargin?: number
    failMargin?: number
    earlyFailMinFrames?: number
    keepPassing?: boolean
  }): {
    start(startDetail?: number): void
    tick(realDtSec: number, info?: { clean?: boolean }): { detail: number | null; p95Ms: number; fps: number; aborted?: string } | null
    cancel(): void
  }
}

/**
 * THE CALIBRATOR: the SDK's DetailCalibrator under THIS policy's window — settle SETTLE_SEC, a clear verdict at
 * SPAN_SEC, conclusive on both sides (pass at ≤ (1 − MARGIN) × bar, fail at ≥ (1 + MARGIN) × bar, near the bar
 * the window extends to EXTEND_SEC and decides), unclean intervals discarded. EARLY FAIL counted against at least
 * EARLY_FAIL_MIN_FRAMES expected frames: below ~3 fps a single frame over the line already fails the step (its p95
 * can't recover) — the SDK's default of 20 waits for a second one, a whole extra frame of a 0.5–2 fps world. Its
 * search: the detail the world is at → one step down → the minimum → bisect; the highest pass is left applied.
 */
export const EARLY_FAIL_MIN_FRAMES = 10
export function sdkCalibrator(Ctor: DetailCalibratorClass): CalibratorFactory {
  return (target, barMs) => {
    const cal = new Ctor(target, {
      bar: { p95Ms: barMs },
      settleSec: WINDOW.SETTLE_SEC,
      sampleSec: WINDOW.SPAN_SEC,
      maxSampleSec: WINDOW.EXTEND_SEC,
      passMargin: 1 - WINDOW.MARGIN,
      failMargin: 1 + WINDOW.MARGIN,
      earlyFailMinFrames: EARLY_FAIL_MIN_FRAMES,
      keepPassing: true
    })
    cal.start(target.activeDetail()) // from where the world is: a first calibration at its authored detail, a re-calibration downward from its setting
    let done = false
    return {
      tick: (realDtSec, clean) => {
        if (done) return undefined
        const r = cal.tick(realDtSec, { clean })
        if (!r) return undefined
        done = true
        if (r.aborted) return null
        return { detail: r.detail === null ? null : Math.round(r.detail * 100) / 100, p95Ms: Math.round(r.p95Ms), fps: Math.round(r.fps * 10) / 10 }
      },
      cancel: () => { if (!done) { done = true; cal.cancel() } }
    }
  }
}

/**
 * ONE WORLD'S DETAIL on one surface: warm up, then — unless this device already knows its setting — calibrate;
 * then hold the setting and keep measuring: a SUSTAINED miss there (WINDOW.WATCH_FAILS conclusive windows in a
 * row — the music got busier for good, not one busy bar) calibrates again DOWNWARD from it. Never raises detail by
 * itself (a re-calibration after an engine or policy change does). Fed the REAL frame interval and:
 *  - `settled` — the world was alone on screen, and the loop RUNNING, for it: frames of a cross-fade or a grid over
 *    it are not the world's, and neither is an interval that spans a PAUSE (the page hidden, the app out of the
 *    foreground — the host's lifecycle/visibility evidence: pauseWatch.ts, the Apple TV's __hostPaused); a change
 *    of that resets the warm-up and drops a calibration in flight.
 *  - `clean` — no world was being built, compiled or torn down across it (LifecycleLedger): a construction stall
 *    is the HOST's cost, not the world's — the sample is skipped, nothing else changes.
 * An interval's LENGTH never classifies it: a settled, clean interval is the world's, however long — a world that
 * takes seconds per frame is exactly the one that must be judged (and leave the screen), never mistaken for a pause.
 */
export class DetailGovernor {
  private settledSec = 0
  private cal: Calibrator | null = null
  private readonly watch = new FrameWindow()
  private watchFails = 0
  private resultV: Calibration | null

  /** `calibrate` — the host's calibrator (sdkCalibrator over its engine's DetailCalibrator); `known` — this device's
   *  remembered setting for the world (undefined = never calibrated here); `start` — where to draw and calibrate from
   *  without one (a GPU-class prior; undefined = the world's authored detail). */
  constructor(private readonly policy: SurfacePolicy, private readonly calibrate: CalibratorFactory, private readonly detail: DetailControl = NO_DETAIL, known?: Calibration, start?: number | null) {
    this.resultV = known ?? null
    const { min, max } = detail.activeDetailRange()
    if (known) detail.setActiveDetail(known.detail ?? min)
    // no setting of its own here yet, but a known START (its GPU class's prior — devicePriors.ts): drawn there from the
    // first frame, and calibrated from there (null = known heavy on this class: its minimum)
    else if (start !== undefined) detail.setActiveDetail(start === null ? min : Math.min(max, Math.max(min, start)))
  }

  private get bar(): number { return this.policy.bar.maxP95Ms }

  tick(realDtSec: number, settled = true, clean = true): void {
    if (!(realDtSec > 0) || !Number.isFinite(realDtSec)) return
    // a build/compile/teardown across this interval — however long — is not the world's cost: discarded, nothing
    // else changes (a world settled on screen stays settled while another one builds; a calibration in flight is
    // told, and discards it too)
    if (settled && !clean) { this.cal?.tick(realDtSec, false); return }
    if (!settled) {
      this.settledSec = 0
      this.watch.clear()
      this.watchFails = 0
      this.cal?.cancel(); this.cal = null
      return
    }
    this.settledSec += realDtSec
    if (this.settledSec <= this.policy.warmupSec) return
    if (this.cal) {
      const r = this.cal.tick(realDtSec, true)
      if (r === undefined) return
      this.cal = null
      this.watch.clear()
      this.watchFails = 0
      if (r === null) return // abandoned (the stage changed under it): measured again from the next settled frames
      this.resultV = r
      // heavy: nothing passes — it is shown at its minimum detail (only because it was picked)
      if (r.detail === null) this.detail.setActiveDetail(this.detail.activeDetailRange().min)
      return
    }
    if (!this.resultV) { this.cal = this.calibrate(this.detail, this.bar); return }
    if (this.resultV.detail === null) return // heavy here: shown at its minimum because it was picked; nothing left
    this.watch.push(realDtSec)
    const ok = judgeWindow(this.watch, this.bar, true)
    if (ok === null) return
    this.watch.clear()
    if (ok) { this.watchFails = 0; return }
    this.watchFails += 1
    if (this.watchFails >= WINDOW.WATCH_FAILS) { this.watchFails = 0; this.cal = this.calibrate(this.detail, this.bar) } // from the setting, downward
  }

  /** The world's setting on this device: null until known (calibrating, or never settled long enough). */
  get result(): Calibration | null { return this.resultV }
  /** The detail in force. */
  get current(): number { return this.detail.activeDetail() }
  get calibrating(): boolean { return this.cal !== null }
}

/** What the ledger needs to see of a renderer: whether it is still building (worldProtocol 1.3 — a staged attach
 *  in flight or its shaders compiling; absent = never building on its own). */
export interface BuildState { readonly isReady?: boolean }

/**
 * CONSTRUCTION, ACCOUNTED: which frame intervals carried a world being built, compiled or torn down — ANY world (the one
 * measured, the one prewarming next, the one being retired). Those frames' intervals are the host's lifecycle cost,
 * not the measured world's, and the governor skips them (`clean`). Fed by the hosts' own lifecycle calls, not by a
 * timer guess:
 *  - `work(fn)` wraps a synchronous lifecycle call (create, attach, a staged attach step, the first render's
 *    compile, dispose, a dispose step): its frame is dirty.
 *  - `watch(mode)` follows a renderer whose build continues ASYNC (isReady === false: a staged attach the host
 *    drains, a shader compile that settles in a timer with a warm render — worlds-three's attachCompile): every
 *    frame while it builds is dirty, AND the frame on which it is found finished — its last step ran between two
 *    frames. `forget(mode)` on dispose.
 * `endFrame()`, once per frame AS ITS INTERVAL CLOSES (at the top of the next frame, before the governor's tick),
 * says whether that interval — the frame drawn and the gap after it, where timers run — AND the one before it were
 * clean: GPU work a build queues (uploads, a link) lands on the present after it, so one quiet interval of slack
 * follows any work.
 */
export class LifecycleLedger {
  private touched = false
  private readonly building = new Set<BuildState>()
  private wasBuilding = false
  private quiet = 0

  work<T>(fn: () => T): T {
    this.touched = true
    try { return fn() } finally { this.touched = true }
  }

  watch(mode: BuildState): void { this.building.add(mode); this.touched = true }
  forget(mode: BuildState): void { if (this.building.delete(mode)) this.touched = true }

  /** Whether any watched renderer is building right now. */
  get busy(): boolean {
    for (const m of this.building) if (m.isReady === false) return true
    return false
  }

  /** As a frame interval closes: was it — and the one before it — free of lifecycle work? */
  endFrame(): boolean {
    const busy = this.busy
    const dirty = this.touched || busy || this.wasBuilding
    this.touched = false
    this.wasBuilding = busy
    this.quiet = dirty ? 0 : this.quiet + 1
    return this.quiet >= 2
  }
}
